import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import {
  type CustomFieldCreateType,
  CustomFieldObject,
} from '~/models/customData';

import { queryKeysCustomField } from './queryKeys';
import { type CustomFieldsListItem } from './useQueryCustomFields';

type CustomFieldsLegacyResponse = {
  items: readonly CustomFieldsListItem[];
  count: number;
};

/**
 * Fetches custom fields legacy data from the API.
 *
 * @returns The custom fields data if successful, null otherwise.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/get_all_custom_fields_custom_field_all_get
 */

export const fetchCustomFieldsLegacy = async () => {
  const { items } = await vestigasApi
    .get(ENDPOINT.CUSTOM_FIELD.GET_ALL_LEGACY())
    .json<CustomFieldsLegacyResponse>();

  return items.map((item) => CustomFieldObject.create(item));
};

// This query uses an deprecated endpoint for fetching ALL custom fields ever, which is not technically possible for large accounts.
export const useQueryCustomFieldsLegacy = <TSelected = CustomFieldCreateType[]>(
  options?: Omit<
    UseQueryOptions<CustomFieldCreateType[], Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CustomFieldCreateType[], Error, TSelected>({
    queryFn: async () => fetchCustomFieldsLegacy(),
    queryKey: queryKeysCustomField.getAllLegacy(),
    ...options,
  });
